import { useQuery } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { IdLabel, ResultChip } from '../../components/atoms.js';
import { ImageLightbox } from '../../components/image-lightbox.js';
import { QueryErrorNotice } from '../../components/query-error-notice.js';
import { executionCommentsApi, executionCommentsKey } from '../../lib/execution-comments-api.js';
import { pickLang } from '../../lib/pick-lang.js';
import { plansApi } from '../../lib/plans-api.js';
import { buildRunTimeline } from './run-timeline.js';

// #35: Inhalt des SidePanels. Die Query hängt am testCaseId und läuft erst, wenn das Panel offen
// ist — die Screenshots kommen als data-URLs und sollen nicht bei jedem Seitenaufbau des
// Testplans über die Leitung gehen.
export function RunHistory({
  projectId,
  planId,
  testCaseId,
}: {
  projectId: string;
  planId: string;
  testCaseId: string;
}) {
  const { t, i18n } = useTranslation();
  const lang = i18n.resolvedLanguage ?? 'de';
  const { data, isLoading } = useQuery({
    queryKey: ['plan-run-history', projectId, planId, testCaseId],
    queryFn: () => plansApi.runHistory(projectId, planId, testCaseId),
    enabled: Boolean(projectId) && Boolean(planId) && Boolean(testCaseId),
  });
  // #193: Kommentare zur Ausführung (Anker Plan + Testfall, nicht der Lauf) stehen zeitlich
  // zwischen den Läufen. Die Historie ist nur lesend: Anlegen/Bearbeiten/Löschen bleibt im
  // Ausführungsfeld. Dieselbe Abfrage wie dort (gleicher Schlüssel); lesende Rollen ohne
  // run.execute dürfen sie lesen. Schlägt sie fehl, bleiben die Läufe sichtbar und der Fehler
  // steht darüber — kein stiller Leerzustand.
  const commentsQuery = useQuery({
    queryKey: executionCommentsKey(projectId, planId, testCaseId),
    queryFn: () => executionCommentsApi.list(projectId, planId, testCaseId),
    enabled: Boolean(projectId) && Boolean(planId) && Boolean(testCaseId),
  });
  const locale = i18n.resolvedLanguage === 'en' ? 'en-GB' : 'de-DE';
  // { runId, index } statt nur index: derselbe Zaehler wuerde sonst fuer jeden Lauf gelten.
  const [zoom, setZoom] = useState<{ runId: string; index: number } | null>(null);
  // Stabil, weil der Effekt in ImageLightbox an onClose haengt und dort focus() ruft —
  // ein Inline-Arrow wuerde bei jedem Render den Fokus erneut ins Overlay reissen (#37).
  const closeZoom = useCallback(() => setZoom(null), []);

  // #193: Nur die Läufe-Abfrage hält die Historie im Ladezustand. Die Kommentar-Abfrage kann mit
  // Retry-Backoff lange dauern; die Läufe sollen dann nicht warten, die Kommentare mischen sich
  // ein, sobald sie da sind.
  if (isLoading) {
    return <p className="text-xs text-text-muted">{t('common.loading')}</p>;
  }
  const runs = data ?? [];
  const comments = commentsQuery.data?.comments ?? [];
  if (runs.length === 0 && comments.length === 0) {
    // Ohne Läufe entscheiden die Kommentare: noch am Laden → Ladehinweis, Fehler → kein Leerhinweis
    // (der Fehler steht unten), erst geladen und leer → „keine Läufe".
    if (commentsQuery.isLoading) {
      return <p className="text-xs text-text-muted">{t('common.loading')}</p>;
    }
    if (!commentsQuery.isError) {
      return <p className="text-xs text-text-muted">{t('plans.noRuns')}</p>;
    }
  }
  const timeline = buildRunTimeline(runs, comments);

  return (
    <div className="space-y-3">
      {commentsQuery.isError && (
        <QueryErrorNotice compact onRetry={() => void commentsQuery.refetch()} />
      )}
      {timeline.map((item) => {
        if (item.kind === 'comment') {
          const c = item.comment;
          return (
            <div
              key={`comment-${c.comment_id}`}
              className="space-y-2 rounded-card border border-[var(--border)] bg-bg-subtle p-3"
            >
              <span className="text-[11px] font-medium text-text-muted">
                {t('execComments.historyLabel')}
              </span>
              <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-text-muted">
                <span className="font-medium text-text-body">
                  {c.author?.name ?? t('execComments.unknownAuthor')}
                </span>
                <span>{new Date(c.created_at).toLocaleString(locale)}</span>
                {c.edited && <span>{t('execComments.edited')}</span>}
              </div>
              <p className="whitespace-pre-wrap text-xs text-text-body">{c.body}</p>
            </div>
          );
        }
        const run = item.run;
        // #208 Nachtrag: `defects` trägt alle Defects des Laufs; ältere Antworten nur `defect`.
        const runDefects = run.defects ?? (run.defect ? [run.defect] : []);
        return (
          <div
            key={run.run_id}
            className="space-y-2 rounded-card border border-[var(--border)] bg-bg-surface p-3"
          >
            <div className="flex items-center justify-between">
              <IdLabel id={run.human_id} />
              <ResultChip status={run.result} />
            </div>
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-text-muted">
              <span>{new Date(run.executed_at).toLocaleString('de-DE')}</span>
              {/* Tester kann null sein: actual_tester_id ist set-null, der Lauf überlebt den User. */}
              <span>{run.tester?.name ?? t('plans.unknownTester')}</span>
            </div>
            {run.failed_step && (
              <p className="text-xs text-text-body">
                {t('plans.failedStep')}: {run.failed_step.step_no} —{' '}
                {pickLang(lang, run.failed_step.description_de, run.failed_step.description_en)}
              </p>
            )}
            {run.result_description && (
              <p className="whitespace-pre-wrap text-xs text-text-body">{run.result_description}</p>
            )}
            {/* #54 (EXE-7): die beim Lauf gewählten Testdaten (Art + Wert). Nur anzeigen, wenn welche
                gewählt wurden — sonst bleibt die Karte ruhig (kein leerer Block). Gelöschte Testdaten
                hat der Server bereits herausgefiltert. Art-Label sprachabhängig, mit Fallback auf die
                jeweils andere Sprache, falls eine Seite leer ist. */}
            {run.used_test_data?.length ? (
              <div className="flex flex-wrap items-center gap-1.5">
                <span className="text-[11px] text-text-muted">{t('plans.usedTestData')}:</span>
                {run.used_test_data.map((d, i) => {
                  const kind =
                    (lang === 'en' ? d.kind_label_en : d.kind_label_de) ??
                    d.kind_label_de ??
                    d.kind_label_en;
                  return (
                    <span
                      // Index im Key, weil used_test_data_ids (jsonb string[]) schema-seitig Duplikate
                      // zulässt — zwei gleiche IDs dürfen nicht zu kollidierenden React-Keys führen.
                      key={`${d.test_data_id}-${i}`}
                      className="rounded-chip border border-[var(--border)] px-2 py-0.5 text-[11px] text-text-body"
                    >
                      {kind ? <span className="text-text-muted">{kind}: </span> : null}
                      {d.value}
                    </span>
                  );
                })}
              </div>
            ) : null}
            {run.screenshots.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {run.screenshots.map((src, i) => (
                  <button
                    key={`${run.run_id}-${i}`}
                    type="button"
                    onClick={() => setZoom({ runId: run.run_id, index: i })}
                    className="block"
                  >
                    <img
                      src={src}
                      alt={t('plans.screenshotAlt', { n: i + 1, run: run.human_id })}
                      className="h-16 w-24 rounded-btn border border-[var(--border)] object-cover"
                    />
                  </button>
                ))}
              </div>
            )}
            {runDefects.length > 0 && (
              <div className="flex flex-wrap items-center gap-2">
                {/* #208 Nachtrag: ALLE Defects des Laufs (verknüpfte notierte Fehler + Auto-Defect). */}
                {runDefects.map((d) => (
                  <Link
                    key={d.defect_id}
                    to={`/defects/${d.defect_id}`}
                    className="inline-block text-xs"
                  >
                    <IdLabel id={d.human_id} />
                  </Link>
                ))}
                {/* #126/#186: „Testfall bestanden, Fehler gemeldet" — der Lauf gilt als bestanden,
                    trägt aber einen gemeldeten Defect. Beim Fehler-Lauf ist der Defect das erwartete
                    Ergebnis. */}
                {run.result === 'successful' && (
                  <span className="rounded-chip border border-[var(--border)] px-2 py-0.5 text-[11px] text-text-body">
                    {t('plans.passedWithDefect')}
                  </span>
                )}
              </div>
            )}
          </div>
        );
      })}
      {zoom &&
        (() => {
          const zoomRun = runs.find((r) => r.run_id === zoom.runId);
          return (
            <ImageLightbox
              // key auf der Inhalts-Identitaet: ohne ihn behaelt das Overlay beim Wechsel des
              // Laufs den Blaetter-Index des vorigen Datensatzes (#37).
              key={zoom.runId}
              images={zoomRun?.screenshots ?? []}
              startIndex={zoom.index}
              // plans.runHistory ("Lauf-Historie") ist die Seitenbeschriftung, nicht die
              // Bildbeschreibung (Branch-Review, kleiner Punkt) — die Lightbox nutzt `label` als
              // aria-label UND als alt-Text, jeder Screenshot wurde dadurch als "Lauf-Historie"
              // vorgelesen. Derselbe sprechende Key wie am Thumbnail (Bildnummer + human_id).
              label={t('plans.screenshotAlt', {
                n: zoom.index + 1,
                run: zoomRun?.human_id ?? '',
              })}
              onClose={closeZoom}
            />
          );
        })()}
    </div>
  );
}
